import moment from 'moment'
import { isString } from 'lodash'

import { BACKEND_DATE_FORMAT, DATE_WITHOUT_TIME, SHORT_EUROPEAN_DATE } from './constants'
import { initEuropeanDate, MONTH_STRING_FORMAT } from './momentjs'
import Clonable from './entities/clonable'

/** @enum {string} */
export const PeriodTypes = Object.freeze({
  YEAR: 'year',
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day'
})

/** @enum {number} */
export const TimePeriods = Object.freeze({
  TWO_MONTHS: 0,
  SIX_MONTHS: 1,
  ONE_YEAR: 2
})

export const TIME_PERIODS_TYPE = [
  {
    timePeriod: TimePeriods.TWO_MONTHS,
    text: '2 months',
    type: PeriodTypes.MONTH,
    quantity: 2
  },
  {
    timePeriod: TimePeriods.SIX_MONTHS,
    text: '6 months',
    type: PeriodTypes.MONTH,
    quantity: 6
  },
  {
    timePeriod: TimePeriods.ONE_YEAR,
    text: 'Year',
    type: PeriodTypes.YEAR
  }
]

export const TIME_PERIODS = [
  {
    timePeriod: TimePeriods.TWO_MONTHS,
    text: '2 months',
    type: PeriodTypes.WEEK,
    quantity: 8
  },
  {
    timePeriod: TimePeriods.SIX_MONTHS,
    text: '6 months',
    type: PeriodTypes.WEEK,
    quantity: 26
  },
  {
    timePeriod: TimePeriods.ONE_YEAR,
    text: 'Year',
    type: PeriodTypes.WEEK,
    quantity: 52
  }
]

export const getNumberOfweeks = (timePeriod) => {
  let numberOfWeeks = 0

  if (timePeriod === TimePeriods.TWO_MONTHS) {
    numberOfWeeks = 8
  } else if (timePeriod === TimePeriods.SIX_MONTHS) {
    numberOfWeeks = 24
  } else if (timePeriod === TimePeriods.ONE_YEAR) {
    numberOfWeeks = 52
  }

  return numberOfWeeks
}

export const getTimePeriod = (date, periodType) => {
  let quantity = (periodType.quantity ?? 1)
  let type = PeriodTypes.WEEK

  if (periodType.type === PeriodTypes.YEAR) {
    quantity = quantity * 12
    type = PeriodTypes.MONTH
  } else if (periodType.type === PeriodTypes.MONTH) {
    quantity = quantity * 4
    type = PeriodTypes.WEEK
  } else if (periodType.type === PeriodTypes.WEEK) {
    quantity = quantity * 7
    type = PeriodTypes.DAY
  }

  return new TimePeriod(date, quantity, type)
}

export const getNewDateByPeriod = (date, periodType, remove = false) => {
  const newDate = date.clone()
  const { type, quantity } = getDisplacementByPeriodType(periodType, remove)

  newDate.add(quantity, type)

  if (periodType.type === PeriodTypes.YEAR) {
    newDate.startOf(PeriodTypes.MONTH)
  }

  return newDate
}

export const getDisplacementByPeriodType = (periodType, remove = false) => {
  const displacement = {
    type: PeriodTypes.WEEK,
    quantity: 1
  }

  if (periodType.type === PeriodTypes.YEAR) {
    displacement.type = PeriodTypes.MONTH
  }

  if (remove) {
    displacement.quantity = 0 - displacement.quantity
  }

  return displacement
}

export const getStartDateByType = (date, type) => {
  const dateString = date.format(DATE_WITHOUT_TIME)
  let startDate

  if (type === PeriodTypes.MONTH || type === PeriodTypes.WEEK) {
    startDate = moment(dateString).startOf(type)
  } else {
    startDate = moment(dateString)
  }

  return startDate
}

export const getEndDateByType = (date, type) => {
  const dateString = date.format(DATE_WITHOUT_TIME)
  let endDate

  if (type === PeriodTypes.MONTH || type === PeriodTypes.WEEK) {
    endDate = moment(dateString).endOf(type)
  } else {
    endDate = moment(dateString)
  }

  return endDate
}

export const getColumnsByPeriod = period => {
  const columns = []

  for (let i = 0; i < period.quantity; i++) {
    const startDate = period.startDate.clone().add(i, period.type)

    columns.push({
      title: period.title(startDate),
      date: startDate
    })
  }

  return columns
}

export const getIndexByPeriodAndDate = (date, period) => {
  let index = 0

  if (date > period.endDate) {
    index = period.length
  } else if (date > period.startDate) {
    index = date.diff(period.startDate, period.type)
  }

  return index
}

export class Period extends Clonable {
  constructor ({ startDate = moment(), endDate = moment(), format = '' } = {}) {
    super()
    this.startDate = moment.isDate(startDate) ? startDate : initEuropeanDate(startDate)
    this.endDate = moment.isDate(endDate) ? endDate : initEuropeanDate(endDate)
    this.format = isString(this.format) ? format : BACKEND_DATE_FORMAT
  }

  getDates (format = undefined) {
    const toApply = isString(format) ? format : this.format

      const startDate = this.startDate.format(toApply)
      const endDate = this.endDate.format(toApply)

    return { startDate, endDate }
  }
}

export class TimePeriod {
  constructor (date, quantity, type) {
    this.type = type
    this.quantity = quantity
    this.startDate = date.clone().startOf(type)
    this.endDate = this.startDate.clone().add(quantity - 1, type).endOf(type)
  }

  title (date = null) {
    const startDate = (date ?? this.startDate)
    let title = ''

    if (this.type === PeriodTypes.MONTH) {
      title = startDate.format(MONTH_STRING_FORMAT)
    } else if (this.type === PeriodTypes.WEEK) {
      title = startDate.format(SHORT_EUROPEAN_DATE)
    }

    return title
  }

  getDates(format = null) {
    return new Period({ ...this, format }).getDates()
  }

  get backendPeriod () {
    return 'P1' + this.type.charAt(0).toUpperCase()
  }

  get length () {
    return this.endDate.diff(this.startDate, this.type)
  }

  get numberOfWeeks () {
    const daysPerWeek = 7
    const monthsPerYear = 12
    const daysPerYear = 365.25

    let weeks

    if (this.type === PeriodTypes.WEEK) {
      weeks = 1
    } else if (this.type === PeriodTypes.MONTH) {
      weeks = daysPerYear / monthsPerYear / daysPerWeek
    } else if (this.type === PeriodTypes.YEAR) {
      weeks = daysPerYear / daysPerWeek
    } else {
      weeks = 0
    }

    return parseInt(weeks * this.quantity)
  }
}
